import PropTypes from "prop-types";
import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import { TYPE_LABELS } from "web/components/screens/contact-list/EditInformationModal.component";
import { OrganizationContext } from "webContext/Organization.context";
const classes = StyleSheet.create({
  root: {
    marginTop: 10,
    marginBottom: 10,
    width: "100%",
    zIndex: "unset",
  },
  inputError: {
    backgroundColor: theme.color.lightGrey,
    borderRadius: 8,
    borderWidth: 2,
    padding: 15,
    boxShadow: "0px 1px 2px rgba(0,0,0,0.2)",
    borderColor: theme.color.error,
    zIndex: 1,
  },
  labelContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 5,
    justifyContent: "space-between",
  },
  startLabelSection: {
    flexDirection: "row",
    alignItems: "center",
  },
  endLabelSection: {
    paddingRight: 5,
    alignItems: "center",
  },
  text: {
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
    margin: 5,
  },
  textDisabled: {
    fontWeight: theme.font.weight.medium,
    marginBottom: 5,
    width: "100%",
    opacity: theme.opacity.disabled,
  },
  textWhite: {
    color: theme.color.white,
  },
  endIconContainer: {
    margin: -8,
    padding: 8,
  },
  iconDisabled: {
    marginBottom: 5,
    marginRight: 3,
    minWidth: 18,
    opacity: theme.opacity.disabled,
  },
  icon: {
    marginBottom: 5,
    color: theme.color.black,
    marginRight: 3,
    minWidth: 18,
  },
  tooltipContainer: {
    backgroundColor: theme.color.white,
    padding: 10,
    position: "absolute",
    zIndex: 99999999,
    marginTop: 25,
    right: "6%",
    borderRadius: 5,
    borderTopEndRadius: 0,
    boxShadow: "0px 1px 2px rgba(0,0,0,0.3)",
  },
  tooltipWrapper: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 2,
    backgroundColor: theme.color.white,
    borderRadius: 6,
    paddingVertical: 8,
    boxShadow: "0px 2px 3px rgba(0,0,0,0.2)",
    zIndex: 100,
  },
  tooltipOption: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  tooltipFullscreen: {
    position: "fixed",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 4,
  },
  requiredText: {
    color: theme.color.error,
  },
  tooltipIcon: {
    margin: -20,
    padding: 20,
    marginBottom: -16,
  },
  secondInputStyle: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 12,
  },
  eyeIcon: {
    position: "absolute",
    right: 15,
    bottom: "20%",
    zIndex: 2,
  },
  flexOne: {
    flex: 1,
  },
});

const LabelInput = forwardRef(function LabelInput(props, ref) {
  const {
    label,
    placeholder,
    isPassword,
    isEmail,
    isPhone,
    disabled,
    value,
    onChangeText,
    onChange,
    required,
    autoFocus,
    tooltip,
    onSubmitEditing,
    returnKeyType,
    blurOnSubmit,
    icon,
    endIcon,
    endIconOnPress,
    readOnly,
    numbersOnly,
    dropdown,
    options = [],
    onSelect,
    multiline,
    hasErrorOnBlur,
    maxLength,
    onTitlePress,
    variant,
    onBlur,
  } = props;

  const { organizationColor } = useContext(OrganizationContext);

  const [isTooltip, setIsTooltip] = useState(false);
  const [hasInputError, setHasInputError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    setHasInputError(hasErrorOnBlur);
  }, [hasErrorOnBlur]);

  const stateClasses = useMemo(
    () =>
      StyleSheet.create({
        readOnlyText: {
          color: theme.color.darkGrey,
        },
        readOnlyContainer: {
          backgroundColor: theme.color.grey,
          color: theme.color.darkGrey,
          padding: "5%",
          borderRadius: theme.card.border.radius,
          boxShadow: "1px 3px 2px rgba(0,0,0,0.2)",
          zIndex: 500,
          flexDirection: "row",
          justifyContent: "space-between",
        },
        readOnlyMulti: {
          backgroundColor: theme.color.grey,
          color: theme.color.darkGrey,
          borderRadius: theme.card.border.radius,
          boxShadow: "1px 3px 2px rgba(0,0,0,0.2)",
          padding: "5%",
          height: 200,
          zIndex: 1,
        },
        disabledInput: {
          backgroundColor: theme.color.background,
          borderRadius: 8,
          borderWidth: 2,
          padding: 15,
          boxShadow: "1px 3px 2px rgba(0,0,0,0.2)",
          borderColor: organizationColor,
          opacity: theme.opacity.disabled,
          zIndex: 1,
        },
      }),
    [organizationColor],
  );

  const inputStyle = useMemo(() => {
    if (hasInputError) {
      return classes.inputError;
    } else if (disabled) {
      return stateClasses.disabledInput;
    } else if (multiline) {
      return {
        backgroundColor: theme.color.background,
        borderRadius: 8,
        borderWidth: 2,
        padding: 15,
        boxShadow: "1px 3px 2px rgba(0,0,0,0.2)",
        height: 200,
        borderColor: organizationColor,
        zIndex: 1,
      };
    } else {
      return {
        backgroundColor: theme.color.background,
        borderRadius: 8,
        borderWidth: 2,
        padding: 15,
        boxShadow: "1px 3px 2px rgba(0,0,0,0.2)",
        borderColor: organizationColor,
        zIndex: 1,
      };
    }
  }, [
    hasInputError,
    disabled,
    multiline,
    stateClasses.disabledInput,
    organizationColor,
  ]);

  const handleNumbersOnlyText = (text) => {
    onChangeText(text.replaceAll(/\D/g, ""));
  };

  const formatPhoneNumber = (text) => {
    const digits = text.replaceAll(/\D/g, "");
    let formatted = digits;
    if (digits.length > 6) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length > 3) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    onChangeText(formatted);
  };

  const toggleTooltip = useCallback(() => {
    setIsTooltip((currentTooltip) => !currentTooltip);
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  return (
    <View style={classes.root}>
      {!readOnly && (
        <View style={classes.labelContainer}>
          <View tabIndex={-1} style={classes.startLabelSection}>
            {icon && (
              <Text
                tabIndex={-1}
                style={disabled ? classes.iconDisabled : classes.icon}
              >
                {icon}
              </Text>
            )}
            <Pressable tabIndex={-1} onPress={onTitlePress}>
              <Text
                style={[
                  disabled ? classes.textDisabled : classes.text,
                  variant === "white" && classes.textWhite,
                ]}
                disabled={disabled}
              >
                {label}
                {required && (
                  <Text style={!disabled && classes.requiredText}> *</Text>
                )}
              </Text>
            </Pressable>
          </View>

          <View style={classes.endLabelSection}>
            <Pressable tabIndex={-1} onPress={toggleTooltip}>
              {tooltip && (
                <Icon
                  name="alert-circle"
                  size={20}
                  color={variant === "white" && theme.color.white}
                  style={classes.tooltipIcon}
                />
              )}
            </Pressable>
          </View>
        </View>
      )}
      {tooltip && isTooltip && (
        <View style={classes.tooltipContainer}>{tooltip}</View>
      )}
      {isTooltip && (
        <TouchableWithoutFeedback tabIndex={-1} onPress={toggleTooltip}>
          <View style={classes.tooltipFullscreen} />
        </TouchableWithoutFeedback>
      )}
      {readOnly && (
        <View>
          <Text style={classes.text}>{label}</Text>
          <View style={stateClasses.readOnlyContainer}>
            <Text style={stateClasses.readOnlyText}>{value}</Text>
            <Pressable
              tabIndex={-1}
              onPressOut={endIconOnPress}
              style={classes.endIconContainer}
            >
              {endIcon}
            </Pressable>
          </View>
        </View>
      )}
      {!readOnly && !dropdown && (
        <TextInput
          placeholder={placeholder}
          placeholderTextColor={theme.color.darkGrey}
          secureTextEntry={isPassword && !showPassword}
          accessibilityLabel={label}
          keyboardType={
            isEmail ? "email-address" : isPhone ? "phone-pad" : "default"
          }
          style={inputStyle}
          editable={!disabled && !readOnly}
          multiline={multiline}
          value={value ?? ""}
          maxLength={maxLength}
          onChangeText={(() => {
            if (isPhone) {
              return formatPhoneNumber;
            }
            if (numbersOnly) {
              return handleNumbersOnlyText;
            }
            return onChangeText;
          })()}
          onChange={onChange}
          autoFocus={autoFocus}
          onSubmitEditing={onSubmitEditing}
          returnKeyType={returnKeyType}
          ref={ref}
          blurOnSubmit={blurOnSubmit}
          onBlur={() => {
            onBlur && onBlur();
            setHasInputError(hasErrorOnBlur);
          }}
        />
      )}
      {!readOnly && dropdown && (
        <View style={{ position: "relative" }}>
          <Pressable
            onPress={() => setIsDropdownOpen((prev) => !prev)}
            style={[inputStyle, classes.secondInputStyle]}
          >
            <Text style={classes.flexOne}>{value || placeholder}</Text>

            <Icon
              name={isDropdownOpen ? "chevron-up" : "chevron-down"}
              size={20}
              color={theme.color.darkGrey}
            />
          </Pressable>

          {isDropdownOpen && (
            <View style={classes.tooltipWrapper}>
              {options.map((opt) => (
                <Pressable
                  key={opt.typeId || opt.key}
                  style={classes.tooltipOption}
                  onPress={() => {
                    onSelect(opt);
                    setIsDropdownOpen(false);
                  }}
                >
                  <Text>{TYPE_LABELS[opt.key] || opt.label}</Text>
                </Pressable>
              ))}
            </View>
          )}
        </View>
      )}
      {isPassword && (
        <Pressable
          onPress={togglePasswordVisibility}
          style={classes.eyeIcon}
          tabIndex={-1}
        >
          <Icon
            name={showPassword ? "eye-off" : "eye"}
            size={22}
            color={theme.color.greyHighlight}
          />
        </Pressable>
      )}
    </View>
  );
});

export default LabelInput;

  LabelInput.propTypes = {
    value: PropTypes.string,
    placeholder: PropTypes.string,
    onChangeText: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    autoFocus: PropTypes.bool,
    onSubmitEditing: PropTypes.func,
    returnKeyType: PropTypes.string,
    blurOnSubmit: PropTypes.bool,
    onBlur: PropTypes.func,
    hasErrorOnBlur: PropTypes.bool,
    readOnly: PropTypes.bool,
    dropdown: PropTypes.bool,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        typeId: PropTypes.any,
        key: PropTypes.any,
        label: PropTypes.string.isRequired,
      })
    ),
    onSelect: PropTypes.func,
    isPassword: PropTypes.bool,
    numbersOnly: PropTypes.bool,
  };